// 환자 자동관리 ① 「먼저 확인할 것」 — 그룹 없는 단일 표 7열 (2026-10-07, 기획서_환자자동관리_통합화면 v3 §3-1 · 법무검토 10-07)
//  열(고정): 사유 | 환자명 | 내용 | 답변 | 시각·남은 날 | 전화 | 동작
//  - 사유 배지 전부 회색 중립(법무 P2-4: 「먼저 볼 답」도 회색). D 값에 따른 색 분기 0. 빨강·주황·아이콘 0.
//  - 답 행: 문구는 응답 labels·행 필드 그대로. [확인 완료]·[이 질문 그만 묻기]·[전체 보기] = useRoadmapInbox 핸들러(접속기록·문구 기존 동일).
//  - 전달 안 됨 행: 사유 = 서버 eventLabel('보내지 못함'), 답변 = resultLabel 그대로, 동작 = 문자열 집합 대조(patientsFirstList.undeliveredActionOf).
//  - 삭제 요청 행: 내용 = deleteRequestReasonLabel 출력 그대로 + 기한(요청일부터 10일). 대표 [상세에서 삭제 처리] / 직원 "치과 대표가 처리".
//  - 금지(법무 RM §2-3): 점수·등급·경보색·결론 라벨·진료 유형·내보내기·인쇄·복사. 보조 글자 12px 이상.
import React from 'react';
import { unifiedStyles as U } from '../../styles/Styles';
import { fmtMs } from '../../services/careRoadmapApi';
import { deleteRequestReasonLabel } from '../../services/clinicPatientsApi';
import { REASON, TEXT, fmtDate } from '../../utils/patientsFirstList';
import { YES_TEXT, TRUNCATED_TEXT } from '../../utils/useRoadmapInbox';

const thStyle = { ...U.tableTh, padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' };
const tdStyle = { ...U.tableTd, padding: '8px 10px', color: '#334155', verticalAlign: 'top' };
const smallBtn = { ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 };
const neutralBadge = { ...U.badge, background: '#f1f5f9', color: '#475569', fontSize: 12 };          // 사유 배지 — 경보색 없음
const subBadge = { ...neutralBadge, fontWeight: 500 };                                                  // 보조 배지 12px(11px 신설 금지)
const muted = { color: '#94a3b8', fontSize: 12 };
const linkBtn = { background: 'transparent', border: 'none', padding: 0, fontSize: 13, fontWeight: 600, color: '#4338ca', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' };

function ClinicFirstList({ rows, loading, pcClock, roadmapOn, isOwner, inbox, failed, onRetry, navigate, truncated }) {
    const { rowBusy, rowNote, phones, ack, stop, reveal, moreBusy, hasMore, loadMore } = inbox;
    const answerRow = (r) => r.reason === REASON.FIRST_LOOK || r.reason === REASON.TO_CHECK;
    const hasDeadlineRow = rows.some(r => r.reason === REASON.DELETE_REQUESTED || r.reason === REASON.UNCONFIRMED);
    const moreBucket = roadmapOn ? (hasMore('first_look') ? 'first_look' : hasMore('to_check') ? 'to_check' : '') : '';

    const nameCell = (r) => {
        if (!r.pid) return <span style={muted}>—</span>;
        const to = r.reason === REASON.UNDELIVERED ? `/patients/${r.pid}#notices` : `/patients/${r.pid}`;
        return <button type="button" onClick={() => navigate(to)} style={linkBtn}>{r.name || '(이름 없음)'}</button>;
    };

    const reasonCell = (r) => {
        const a = r.src || {};
        return (
            <div>
                <span style={neutralBadge}>{r.reasonLabel}</span>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
                    {answerRow(r) && a.isRequery && <span style={subBadge}>재질문</span>}
                    {answerRow(r) && a.repeatYesLabel && <span style={subBadge}>{a.repeatYesLabel}</span>}
                    {answerRow(r) && a.permissionDeniedLabel && <span style={subBadge}>{a.permissionDeniedLabel}</span>}
                    {answerRow(r) && a.phraseStopped && <span style={subBadge}>그만 묻기 적용</span>}
                    {r.daysLeftLabel && <span style={{ ...subBadge, fontWeight: r.daysLeft === 0 ? 700 : 500 }}>{r.daysLeftLabel}</span>}
                </div>
            </div>
        );
    };

    const contentCell = (r) => {
        if (answerRow(r)) return <div>{r.src?.questionText || ''}</div>;
        if (r.reason === REASON.DELETE_REQUESTED) return <div>{TEXT.deleteContent(deleteRequestReasonLabel(r.src?.deleteRequestReason), fmtDate(new Date(r.deadline)))}</div>;
        return (
            <div>
                <div>{r.content || ''}</div>
                {r.contentSub && <div style={{ fontSize: 12, color: '#64748b' }}>{r.contentSub}</div>}
            </div>
        );
    };

    const answerCell = (r) => {
        if (answerRow(r)) {
            const a = r.src || {};
            const extras = a.extraAnswers && typeof a.extraAnswers === 'object' ? Object.entries(a.extraAnswers) : [];
            return (
                <div>
                    <b style={{ color: '#0f172a' }}>{a.answer === 'yes' ? YES_TEXT : (a.answer || '')}</b>
                    {extras.map(([k, v]) => <div key={k} style={{ fontSize: 12.5, color: '#64748b' }}>{`${v?.text || k}: ${v?.label || v?.value || ''}`}</div>)}
                </div>
            );
        }
        if (r.reason === REASON.UNDELIVERED) return <div>{r.answer || ''}</div>;
        return <span style={muted}>—</span>;
    };

    const whenCell = (r) => {
        if (answerRow(r)) {
            const a = r.src || {};
            const acked = a.status === 'acknowledged';
            return (
                <div style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                    <div>{`답 ${fmtMs(a.answeredAt, true)}`}</div>
                    {a.sentAt && <div style={{ color: '#94a3b8' }}>{`발송 ${fmtMs(a.sentAt)}`}</div>}
                    {acked && a.ackAt && <div style={{ color: '#94a3b8' }}>{`확인 ${fmtMs(a.ackAt)}${a.ackByRole === 'staff' ? ' · 직원' : a.ackByRole === 'owner' ? ' · 대표' : ''}`}</div>}
                    {a.deleteAt && <div style={{ color: '#94a3b8' }}>{`삭제 예정 ${fmtMs(a.deleteAt)}`}</div>}
                </div>
            );
        }
        return <div style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{r.when || <span style={muted}>-</span>}</div>;
    };

    const phoneCell = (r) => {
        if (!answerRow(r)) return <span style={muted}>-</span>;
        const a = r.src || {};
        const phone = phones[a.pid];
        if (phone) return <span>{phone}</span>;
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                <span>{a.phoneLast4 ? `***-${a.phoneLast4}` : '번호 없음'}</span>
                {a.phoneLast4 && <button type="button" disabled={!!rowBusy} onClick={() => reveal(a)} style={smallBtn}>{rowBusy === `${a.pid}:phone` ? '조회 중...' : '전체 보기'}</button>}
            </span>
        );
    };

    const actionCell = (r) => {
        if (answerRow(r)) {
            const a = r.src || {};
            const acked = a.status === 'acknowledged';
            return (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {acked
                        ? <span style={neutralBadge}>확인 완료</span>
                        : <button type="button" disabled={!!rowBusy} onClick={() => ack(a)} style={{ ...U.buttonPrimary, padding: '3px 10px', fontSize: 12 }}>{rowBusy === `${a.fbId}:ack` ? '처리 중...' : '확인 완료'}</button>}
                    <button type="button" disabled={!!rowBusy || a.phraseStopped === true} onClick={() => stop(a)}
                        style={{ ...smallBtn, opacity: a.phraseStopped ? 0.5 : 1, cursor: a.phraseStopped ? 'default' : 'pointer' }}>{rowBusy === `${a.fbId}:stop` ? '처리 중...' : '이 질문 그만 묻기'}</button>
                </div>
            );
        }
        if (r.reason === REASON.DELETE_REQUESTED || r.reason === REASON.UNCONFIRMED) {
            return isOwner
                ? <button type="button" onClick={() => navigate(`/patients/${r.pid}`)} style={smallBtn}>{TEXT.actionOwnerDelete}</button>
                : <span style={{ fontSize: 12.5, color: '#64748b' }}>{TEXT.actionStaffDelete}</span>;
        }
        return <span style={{ fontSize: 12.5, color: '#64748b' }}>{r.action || ''}</span>;
    };

    return (
        <div>
            {failed.length > 0 && (
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                    {TEXT.partialError}
                    <button type="button" onClick={onRetry} style={{ ...smallBtn, marginLeft: 6 }}>다시 시도</button>
                </div>
            )}
            {truncated && <div style={{ background: '#f1f5f9', color: '#475569', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 10 }}>{TRUNCATED_TEXT}</div>}
            {loading && rows.length === 0 ? (
                <div style={{ ...U.card, padding: '24px 12px', fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>불러오는 중...</div>
            ) : rows.length === 0 ? (
                <div style={{ ...U.card, padding: '24px 12px', fontSize: 13, color: '#64748b', textAlign: 'center' }}>{roadmapOn ? TEXT.emptyPilot : TEXT.emptyBasic}</div>
            ) : (
                <div style={{ ...U.card, padding: 0, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: 118 }}>사유</th>
                                    <th style={{ ...thStyle, width: 72 }}>환자명</th>
                                    <th style={thStyle}>내용</th>
                                    <th style={{ ...thStyle, width: 190 }}>답변</th>
                                    <th style={{ ...thStyle, width: 140 }}>시각 · 남은 날</th>
                                    <th style={{ ...thStyle, width: 140 }}>전화</th>
                                    <th style={{ ...thStyle, width: 200 }}>동작</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => {
                                    const note = answerRow(r) ? rowNote[r.src?.fbId] : null;
                                    return (
                                        <React.Fragment key={r.key}>
                                            <tr>
                                                <td style={tdStyle}>{reasonCell(r)}</td>
                                                <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{nameCell(r)}</td>
                                                <td style={tdStyle}>{contentCell(r)}</td>
                                                <td style={tdStyle}>{answerCell(r)}</td>
                                                <td style={tdStyle}>{whenCell(r)}</td>
                                                <td style={tdStyle}>{phoneCell(r)}</td>
                                                <td style={tdStyle}>{actionCell(r)}</td>
                                            </tr>
                                            {note && (
                                                <tr>
                                                    <td colSpan={7} style={{ ...tdStyle, padding: '4px 10px 8px', fontSize: 12.5, color: note.type === 'err' ? '#b91c1c' : '#475569', background: '#f8fafc' }}>{note.text}</td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {moreBucket && (
                        <div style={{ padding: '8px 12px', borderTop: '1px solid #f1f5f9' }}>
                            <button type="button" disabled={!!moreBusy} onClick={() => loadMore(moreBucket)} style={smallBtn}>{moreBusy ? '불러오는 중...' : '더 보기'}</button>
                        </div>
                    )}
                </div>
            )}
            {pcClock && hasDeadlineRow && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>{TEXT.pcClockNote}</div>}
        </div>
    );
}

export default ClinicFirstList;
